// src/components/SquarePaymentForm.js

import React, { useEffect, useState, useRef } from 'react';
import { loadSquareSdk, cleanupSquareElements } from '../utils/square-utils';

function SquarePaymentForm({ 
  onPaymentComplete, 
  total, 
  orderData, 
  showPayButton = true, 
  onCardReady
}) {
  const [card, setCard] = useState(null);
  const [paymentForm, setPaymentForm] = useState(null);
  const [errorMessages, setErrorMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // Create a ref to track component mount status
  const isMounted = useRef(true);
  // Create a unique ID for this instance with more entropy
  const cardContainerId = useRef(`square-card-container-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  
  // Store callback in ref to avoid dependency issues
  const onCardReadyRef = useRef(onCardReady);
  
  // Update ref when prop changes
  useEffect(() => {
    onCardReadyRef.current = onCardReady;
  }, [onCardReady]);
  
  // Cleanup function
  const cleanup = () => {
    if (card) {
      try {
        console.log('Destroying Square card instance');
        card.destroy();
      } catch (e) {
        console.warn('Error destroying Square card (may already be destroyed):', e);
      }
    }
    setCard(null);
    setPaymentForm(null);
  };
  
  // Set up mount/unmount handling
  useEffect(() => {
    isMounted.current = true;
    
    return () => {
      isMounted.current = false;
      cleanup();
    };
  }, []);
  
  // Load Square SDK and initialize payment form
  useEffect(() => {
    // Clean up any existing card first
    cleanup();
    
    const initializeSquare = async () => {
      try {
        // Load Square SDK
        const Square = await loadSquareSdk();
        if (!Square || !isMounted.current) {
          return;
        }

        // Wait for DOM to be ready
        await new Promise(resolve => setTimeout(resolve, 200));

        // Validate credentials
        if (!process.env.REACT_APP_SQUARE_APP_ID || !process.env.REACT_APP_SQUARE_LOCATION_ID) {
          throw new Error('Square credentials not configured');
        }

        // Initialize Square payments
        console.log('Initializing Square with PRODUCTION credentials');
        const payments = Square.payments(
          process.env.REACT_APP_SQUARE_APP_ID, 
          process.env.REACT_APP_SQUARE_LOCATION_ID
        );

        // Initialize card payment method with simplified styling
        const newCard = await payments.card({
          style: {
            input: {
              fontSize: '16px',
              fontFamily: 'Arial',
              color: '#4a3f35',
              backgroundColor: 'transparent'
            },
            'input::placeholder': {
              color: '#999999'
            },
            'input.is-focus': {
              color: '#4a3f35'
            },
            'input.is-error': {
              color: '#FF6B6B'
            }
          }
        });
        
        // Make sure component is still mounted and container exists
        if (!isMounted.current) return;
        
        // Wait a bit more for DOM element to be fully ready
        await new Promise(resolve => setTimeout(resolve, 100));
        
        const cardContainer = document.getElementById(cardContainerId.current);
        if (!cardContainer) {
          console.error('Square card container element not found:', cardContainerId.current);
          setErrorMessages(['Payment form container not found']);
          return;
        }
        
        if (cardContainer.innerHTML !== '') {
          console.warn('Square card container is not empty, clearing it');
          cardContainer.innerHTML = '';
        }
        
        if (cardContainer) {
          await newCard.attach(`#${cardContainerId.current}`);
          
          if (!isMounted.current) {
            newCard.destroy();
            return;
          }
          
          setCard(newCard);
          setPaymentForm(payments);
          
          console.log('Square payment form initialized successfully');
          
          // Notify parent that card is ready
          if (onCardReadyRef.current) {
            onCardReadyRef.current(newCard, payments);
          }
        } else {
          console.error('Square card container element not found or not empty');
          setErrorMessages(['Payment form element not found']);
        }
      } catch (error) {
        if (isMounted.current) {
          console.error('Square initialization error:', error);
          setErrorMessages([error.message]);
        }
      }
    };

    initializeSquare();
  }, []); // Keep empty dependency array

  // Handle form submission
  const handlePaymentSubmit = async (event) => {
    event.preventDefault();
    
    if (!card || !paymentForm) {
      setErrorMessages(['Payment form not initialized']);
      return;
    }
    
    setIsLoading(true);
    setErrorMessages([]);
    
    try {
      console.log('Processing payment in PRODUCTION mode');
      
      // Get a payment token from Square - only call tokenize once
      const result = await card.tokenize();
      
      if (result.status === 'OK') {
        console.log('Payment token generated successfully');
        
        // Send the token to your server
        const response = await fetch('https://s9l70ztz3c.execute-api.us-east-1.amazonaws.com/default/bucks-customer-order-place-process-post', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            sourceId: result.token,
            amount: total,
            orderData: orderData
          }),
        });
        
        const paymentResponse = await response.json();
        
        if (paymentResponse.success) {
          console.log('Payment completed successfully:', paymentResponse.orderNumber);
          onPaymentComplete({
            success: true,
            orderId: paymentResponse.orderId,
            orderNumber: paymentResponse.orderNumber,
            transactionId: paymentResponse.transactionId
          });
        } else {
          console.error('Payment failed:', paymentResponse.error);
          setErrorMessages([paymentResponse.error || 'Payment failed']);
        }
      } else {
        console.error('Payment tokenization failed:', result.errors);
        setErrorMessages([result.errors[0].message]);
      }
    } catch (error) {
      console.error('Payment submission error:', error);
      setErrorMessages([error.message || 'An error occurred during payment processing']);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="square-payment-form">
      <div className="payment-form-header">
        <h3>Payment Information</h3>
        <p className="production-notice" style={{color: '#28a745', fontSize: '0.9rem', fontWeight: 'bold'}}>
          🔒 Live Payment Processing
        </p>
      </div>
      
      <div className="card-container">
        <div id={cardContainerId.current}></div>
      </div>
      
      {errorMessages.length > 0 && (
        <div className="payment-errors">
          {errorMessages.map((message, index) => (
            <p key={index} className="error-message">{message}</p>
          ))}
        </div>
      )}
      
      {showPayButton && (
        <button 
          type="button" 
          onClick={handlePaymentSubmit}
          className="payment-button"
          disabled={isLoading || !card}
        >
          {isLoading ? 'Processing Payment...' : 'Pay Now'}
        </button>
      )}
    </div>
  );
}

export default SquarePaymentForm;